import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, List, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  getNoticesListNoticesQueryKey,
  useNoticesAcknowledge,
  useNoticesListNotices,
} from '@/api/generated/endpoints/notices/notices';
import type { NoticeOut } from '@/api/generated/model';
import { correctionLines } from '@/lib/changes';
import { qtyText } from '@/lib/units';

const DATETIME = 'DD.MM.YYYY HH:mm';

/** Что исправили — одной фразой: товар и партия или номер отгрузки. */
function headline(t: TFunction, n: NoticeOut): string {
  const ctx = { product: n.product ?? '', batch: n.batch_id, shipment: n.shipment ?? '' };
  if (n.kind === 'cancel_movement') {
    const m = n.changes.movement as { type?: string; qty?: string } | undefined;
    const qty = m?.qty ? qtyText(t, m.qty.replace(/^-/, ''), n.uom ?? 'kg') : '';
    const what = m?.type === 'writeoff' ? 'writeoff' : 'adjustment';
    return t(`notices.text.cancel_${what}`, { ...ctx, qty });
  }
  return t(`notices.text.${n.kind}`, ctx);
}

/**
 * Сотруднику: его действие исправил администратор (ADR-0012 п. 6).
 * Карточка висит, пока сотрудник не нажмёт «Понятно».
 */
export function CorrectionNotices() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notices = useNoticesListNotices();
  const ack = useNoticesAcknowledge();
  const items = notices.data ?? [];
  if (items.length === 0) return null;

  return (
    <Card
      title={t('notices.title', { count: items.length })}
      styles={{ header: { background: 'var(--ant-color-warning-bg, #fffbe6)' } }}
    >
      <List
        dataSource={items}
        renderItem={(n) => (
          <List.Item
            actions={[
              <Button
                key="ok"
                type="primary"
                loading={ack.isPending && ack.variables.correctionId === n.id}
                onClick={() => {
                  void ack
                    .mutateAsync({ correctionId: n.id })
                    .then(() =>
                      queryClient.invalidateQueries({ queryKey: getNoticesListNoticesQueryKey() }),
                    );
                }}
              >
                {t('notices.ok')}
              </Button>,
            ]}
          >
            <Space direction="vertical" size={2}>
              <Typography.Text strong>{headline(t, n)}</Typography.Text>
              {correctionLines(t, n.changes, n.uom).map((line) => (
                <Typography.Text key={line}>{line}</Typography.Text>
              ))}
              <Typography.Text type="secondary">
                {t('notices.by', { who: n.actor, at: dayjs(n.at).format(DATETIME) })}
              </Typography.Text>
              <Typography.Text>{t('notices.reason', { reason: n.reason })}</Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
}
